/**
 * Global design tokens for Salonite365 frontend.
 * SINGLE SOURCE OF TRUTH — import these (or the injected CSS vars) instead of
 * hardcoding hex values in components.
 *
 * JS usage:    import { colors, shadows, radii, gradients, type } from '../lib/theme';
 * CSS usage:   color: var(--color-ink-900);  background: var(--gradient-primary);
 *
 * The `cssVariables` block is injected once, globally, in App.jsx.
 *
 * ── Direction: balanced modern refresh, unified around ONE purple lineage with
 *    a warm coral/gold accent. Softer than Fresha; not a clone. ──
 *
 * Back-compat: every legacy export name and every legacy CSS var name from the
 * previous theme is preserved, so nothing that already reads them breaks.
 */

/* ────────────────────────────────────────────────────────────────────────── *
 * RAMPS
 * ────────────────────────────────────────────────────────────────────────── */

/** Primary purple ramp — signature brand color. Base = 500. */
export const primary = {
  50:  '#f6f3fd',
  100: '#ece5fa',
  200: '#d8cef2',
  300: '#bda6ec',
  400: '#a78bdc',
  500: '#8b6fd4', // ← primary
  600: '#7856c2', // hover / press
  700: '#634aa0',
  800: '#4a377a',
  900: '#2f2451',
};

/** Warm coral accent ramp — the distinct pop (kept instead of Fresha's lime). */
export const accent = {
  soft:   '#f7b9a1',
  400:    '#f4956f', // ← accent
  500:    '#ee7a4e', // accentStrong (hover / press)
  bg:     'rgba(244,149,111,0.10)',
};

/** Ink / neutral ramp — replaces the four mismatched dark colors site-wide. */
export const ink = {
  900: '#1e1b2e', // headings + footer ground
  700: '#34304a',
  500: '#5d5a76', // body text
  400: '#8f8ca6', // muted text
  300: '#bbb8cc', // faint text / disabled
  border:       '#e9e6f2',
  borderStrong: '#dcd8ec',
};

/* ────────────────────────────────────────────────────────────────────────── *
 * SEMANTIC COLORS
 * ────────────────────────────────────────────────────────────────────────── */

export const colors = {
  // Primary brand (semantic aliases into the ramp)
  primary:       primary[500],
  primaryStrong: primary[600],
  primaryDark:   primary[800],
  purple:        '#a346d5', // vivid magenta pop — use sparingly (gradients/CTAs)
  purpleLight:   'rgba(163,70,213,0.10)',

  // Legacy lavender aliases (kept for back-compat; now mapped to the ramp)
  lavender:      primary[400],
  lavenderLight: primary[50],
  lavenderMid:   primary[200],

  // Accent — warm
  coral:      accent[400],
  coralStrong:accent[500],
  coralSoft:  accent.soft,
  coralLight: '#fff5f2',
  gold:       '#f5c842',
  goldLight:  'rgba(245,200,66,0.14)',

  // Supporting hues (category chips, search icons) — harmonized, non-core
  blue:       '#7aa5e0',
  blueLight:  '#eef4fd',
  teal:       '#3dc0c0',
  tealLight:  '#eafafa',
  green:      '#5cc38f',
  greenLight: 'rgba(92,195,143,0.12)',

  // Text (semantic; back-compat names retained)
  textDark: ink[900],
  textMid:  ink[500],
  textLight:ink[400],

  // Surfaces
  card:          '#ffffff',
  surfaceSubtle: '#faf9fe',
  surfaceMuted:  '#f3f1fb', // replaces the flat #F4F6FB city block
  border:        ink.border,
  borderStrong:  ink.borderStrong,

  // Page background — one unified purple-family wash (replaces the 3 grounds)
  pageBg: 'linear-gradient(135deg, #f4f1fb 0%, #efeafb 48%, #f2f0fd 100%)',
};

/* ────────────────────────────────────────────────────────────────────────── *
 * SCALES
 * ────────────────────────────────────────────────────────────────────────── */

export const radii = {
  xs:    '8px',
  sm:    '10px',
  md:    '14px',
  lg:    '18px',
  xl:    '22px',
  '2xl': '28px',
  full:  '9999px',
};

export const shadows = {
  card:    '0 2px 24px rgba(107,88,180,0.10), 0 1px 4px rgba(107,88,180,0.06)',
  cardHov: '0 12px 36px rgba(107,88,180,0.18)',
  primary: '0 4px 16px rgba(139,111,212,0.40)',
  accent:  '0 4px 16px rgba(238,122,78,0.32)',
  sm:      '0 2px 8px rgba(30,27,46,0.06)',
  lg:      '0 16px 48px rgba(30,27,46,0.12)',
  focus:   '0 0 0 3px rgba(139,111,212,0.30)',
};

export const gradients = {
  primary:       'linear-gradient(135deg, #a78bdc, #8b6fd4)',
  primaryStrong: 'linear-gradient(135deg, #8b6fd4, #7856c2)',
  accent:        'linear-gradient(135deg, #f7b9a1, #f4956f)',
  vivid:         'linear-gradient(135deg, #c68af0, #a346d5)',
  gold:          'linear-gradient(135deg, #ffc75f 0%, #d1b7f3 50%, #a346d5 100%)',
  dark:          'linear-gradient(135deg, #2f2451 0%, #1e1b2e 100%)',
  page:          'linear-gradient(135deg, #f4f1fb 0%, #efeafb 48%, #f2f0fd 100%)',
};

export const transitions = {
  fast: 'all 0.15s ease',
  base: 'all 0.2s ease',
  slow: 'all 0.35s ease',
};

/** Type scale — display-forward, tight tracking. DM Sans (max weight 800). */
export const type = {
  family:   "'DM Sans', ui-sans-serif, system-ui, -apple-system, sans-serif",
  serif:    "'Playfair Display', serif",
  weightBody:    400,
  weightMedium:  500,
  weightSemibold:600,
  weightBold:    700,
  weightDisplay: 800,
  trackTight:   '-0.02em',
  trackTighter: '-0.03em',
  trackWide:    '0.18em',
  // fluid sizes
  display: 'clamp(2.4rem, 5vw, 4rem)',
  h1:      'clamp(1.9rem, 3.6vw, 3rem)',
  h2:      'clamp(1.4rem, 3vw, 2rem)',
  h3:      'clamp(1.05rem, 2vw, 1.35rem)',
  body:    '0.95rem',
  small:   '0.82rem',
  eyebrow: '0.68rem',
};

/* ────────────────────────────────────────────────────────────────────────── *
 * CSS CUSTOM PROPERTIES — injected once in App.jsx.
 * Every legacy var name is preserved; new tokens are additive.
 * ────────────────────────────────────────────────────────────────────────── */
export const cssVariables = `
  :root {
    /* ── Primary ramp ── */
    --color-primary-50:   ${primary[50]};
    --color-primary-100:  ${primary[100]};
    --color-primary-200:  ${primary[200]};
    --color-primary-300:  ${primary[300]};
    --color-primary-400:  ${primary[400]};
    --color-primary-500:  ${primary[500]};
    --color-primary-600:  ${primary[600]};
    --color-primary-700:  ${primary[700]};
    --color-primary-800:  ${primary[800]};
    --color-primary-900:  ${primary[900]};
    --color-primary:       ${colors.primary};
    --color-primary-strong:${colors.primaryStrong};
    --color-primary-dark:  ${colors.primaryDark};

    /* ── Ink / neutral ramp ── */
    --color-ink-900:     ${ink[900]};
    --color-ink-700:     ${ink[700]};
    --color-ink-500:     ${ink[500]};
    --color-ink-400:     ${ink[400]};
    --color-ink-300:     ${ink[300]};
    --color-border:        ${ink.border};
    --color-border-strong: ${ink.borderStrong};

    /* ── Accent (warm) ── */
    --color-accent:        ${accent[400]};
    --color-accent-strong: ${accent[500]};
    --color-accent-soft:   ${accent.soft};
    --color-accent-bg:     ${accent.bg};

    /* ── Surfaces ── */
    --color-card:           ${colors.card};
    --color-surface-subtle: ${colors.surfaceSubtle};
    --color-surface-muted:  ${colors.surfaceMuted};

    /* ── Legacy aliases (DO NOT REMOVE — already consumed) ── */
    --color-lavender:       ${colors.lavender};
    --color-lavender-light: ${colors.lavenderLight};
    --color-purple:         ${colors.purple};
    --color-purple-light:   ${colors.purpleLight};
    --color-gold:           ${colors.gold};
    --color-coral:          ${colors.coral};
    --color-blue:           ${colors.blue};
    --color-green:          ${colors.green};
    --color-text-dark:      ${colors.textDark};
    --color-text-mid:       ${colors.textMid};
    --color-text-light:     ${colors.textLight};

    /* ── Radii ── */
    --radius-xs:   ${radii.xs};
    --radius-sm:   ${radii.sm};
    --radius-md:   ${radii.md};
    --radius-lg:   ${radii.lg};
    --radius-xl:   ${radii.xl};
    --radius-2xl:  ${radii['2xl']};
    --radius-full: ${radii.full};

    /* ── Shadows ── */
    --shadow-card:     ${shadows.card};
    --shadow-card-hov: ${shadows.cardHov};
    --shadow-primary:  ${shadows.primary};
    --shadow-accent:   ${shadows.accent};
    --shadow-sm:       ${shadows.sm};
    --shadow-lg:       ${shadows.lg};
    --shadow-focus:    ${shadows.focus};

    /* ── Gradients ── */
    --gradient-primary:        ${gradients.primary};
    --gradient-primary-strong: ${gradients.primaryStrong};
    --gradient-accent:         ${gradients.accent};
    --gradient-vivid:          ${gradients.vivid};
    --gradient-gold:           ${gradients.gold};
    --gradient-dark:           ${gradients.dark};
    --gradient-page:           ${gradients.page};

    /* ── Type ── */
    --font-display-weight: ${type.weightDisplay};
    --track-tight:   ${type.trackTight};
    --track-tighter: ${type.trackTighter};
    --track-wide:    ${type.trackWide};
    --text-display: ${type.display};
    --text-h1:      ${type.h1};
    --text-h2:      ${type.h2};
    --text-h3:      ${type.h3};

    /* ── Transitions ── */
    --transition-fast: ${transitions.fast};
    --transition-base: ${transitions.base};
    --transition-slow: ${transitions.slow};
  }
`;